export interface RectifierTimePoint {
	interval: number;
	value: number;
	cycle: boolean;
}

export interface RectifierProfileStep {
	start: number;
	length: number;
	value: number;
}

export function createRectifierProfileSteps(type: string, timePoints: RectifierTimePoint[], processTime: number, programNumber: number): RectifierProfileStep[] {
	if (type == 'OFF') {
		return [];
	}

	if (type == 'BUILT_IN') {
		return [{start: 0, length: 0, value: programNumber}];
	}

	const bodyEnd = bodyEndIndex(timePoints);
	const cycleStart = cycleStartIndex(timePoints, bodyEnd);
	const cycleDuration = cycleStart == null ? 0 : durationBetween(timePoints, cycleStart, bodyEnd);

	const out: RectifierProfileStep[] = [];
	let outDuration = 0;

	for (let index = 0; index < bodyEnd && outDuration < processTime; ++index) {
		const timePoint = timePoints[index];

		out.push({start: outDuration, length: timePoint.interval, value: timePoint.value});
		outDuration += timePoint.interval * 1000;

		if (index == bodyEnd - 1 && cycleStart != null && cycleDuration > 0) {
			index = cycleStart - 1;
		}
	}

	if (cycleStart == null && out.length > 0 && out[out.length - 1].value != 0) {
		out.push({start: outDuration, length: 0, value: 0});
	}

	if (bodyEnd < timePoints.length) {
		const endTime = processTime;
		for (const tailStep of timePoints.slice(bodyEnd)) {
			const tailAt = endTime + tailStep.interval * 1000;
			while (out.length > 0 && out[out.length - 1].start >= tailAt) {
				out.pop();
			}

			out.push({start: tailAt, length: 0, value: tailStep.value});
		}
	}

	for (let i = out.length - 1; i > 0; --i) {
		const current = out[i];
		const previous = out[i - 1];

		if (previous.start == current.start && previous.length == 0 && current.length == 0) {
			out.splice(i - 1, 1);
			continue;
		}

		if (previous.value == current.value) {
			out.splice(i, 1);
		}
	}

	return out;
}

export function previewProcessTime(timePoints: RectifierTimePoint[], cycleIterations: number = 2): number {
	const bodyEnd = bodyEndIndex(timePoints);
	const cycleStart = cycleStartIndex(timePoints, bodyEnd);
	const cycleDuration = cycleStart == null ? 0 : durationBetween(timePoints, cycleStart, bodyEnd);
	const bodyDuration = durationBetween(timePoints, 0, bodyEnd);
	const tailDuration = -durationBetween(timePoints, bodyEnd, timePoints.length);

	let result = bodyDuration;
	if (cycleStart != null && cycleDuration > 0) {
		result += cycleDuration * cycleIterations;
	}

	return result + tailDuration;
}

export function activeCycleStart(timePoints: RectifierTimePoint[]): number | null {
	return cycleStartIndex(timePoints, bodyEndIndex(timePoints));
}

export interface ChartPoint {
	time: number;
	value: number;
}

/**
 * Шаг означает линейный переход от текущего значения к step.value за step.length секунд.
 * Следующий шаг может начаться до окончания текущего - тогда текущий обрезается,
 * а стартовое значение следующего берется интерполяцией в момент его старта.
 * Последнее значение держится до конца времени обработки.
 */
export function buildChartPoints(steps: RectifierProfileStep[], processTime: number): ChartPoint[] {
	const points: ChartPoint[] = [];

	let prevStart = 0;
	let prevEnd = 0;
	let prevFrom = 0;
	let prevTo = 0;

	for (const step of steps) {
		const start = step.start / 1000;
		const end = start + step.length;

		let from: number;
		if (start < prevEnd) {
			from = prevFrom + (start - prevStart) / (prevEnd - prevStart) * (prevTo - prevFrom);
			points[points.length - 1] = {time: start, value: from};
		} else {
			from = prevTo;
		}

		points.push({time: start, value: from});
		points.push({time: end, value: step.value});

		prevStart = start;
		prevFrom = from;
		prevTo = step.value;
		prevEnd = end;
	}

	const endSec = processTime / 1000;
	if (points.length > 0 && points[points.length - 1].time < endSec) {
		points.push({time: endSec, value: points[points.length - 1].value});
	}

	return points;
}

function bodyEndIndex(timePoints: RectifierTimePoint[]): number {
	const index = timePoints.findIndex(p => p.interval < 0);
	return index == -1 ? timePoints.length : index;
}

function cycleStartIndex(timePoints: RectifierTimePoint[], bodyEnd: number): number | null {
	const index = timePoints.findIndex(p => p.cycle);
	return index != -1 && index < bodyEnd ? index : null;
}

function durationBetween(timePoints: RectifierTimePoint[], from: number, to: number): number {
	let duration = 0;
	for (let i = from; i < to; ++i) {
		duration += timePoints[i].interval * 1000;
	}
	return duration;
}
